import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ZONES, citySlug } from "@/lib/zones";
import { Button } from "@/components/ui/button";
import OfferCard from "@/components/OfferCard";
import { FREE_ORG_TEXT } from "@/components/FreeOrgBadge";
import { FounderPriceInline } from "@/components/FounderPriceBlock";
import {
  MapPin, Store, Tag, ArrowRight, Handshake, ShieldCheck,
  ChevronLeft, Building2, Award,
} from "lucide-react";

// Coordonnées (dupliquées ici pour rester cohérent avec DepartmentMap)
const POLE_COORDS = {
  chartres:   { lat: 48.4470, lng: 1.4890 },
  chateaudun: { lat: 48.0733, lng: 1.3288 },
  dreux:      { lat: 48.7375, lng: 1.3661 },
  brou:       { lat: 48.2159, lng: 1.1636 },
  epernon:    { lat: 48.6089, lng: 1.6790 },
  nogent:     { lat: 48.3244, lng: 0.8206 },
  bonneval:   { lat: 48.1786, lng: 1.3897 },
  courville:  { lat: 48.4500, lng: 1.2403 },
  illiers:    { lat: 48.3006, lng: 1.2475 },
};

export default function PolePage() {
  const { poleId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const zone = useMemo(() => ZONES.find((z) => z.id === poleId), [poleId]);
  const coords = POLE_COORDS[poleId];

  useEffect(() => {
    if (!zone) { setErr("Pôle introuvable."); return; }
    setErr("");
    api.get(`/public/pole/${poleId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Erreur de chargement."));
  }, [poleId, zone]);

  // SEO dynamique + JSON-LD LocalBusiness
  useEffect(() => {
    if (!zone) return;
    const title = `Bons plans ${zone.name} | Commerces & réductions locales | PROXIreduc-28`;
    const desc = `Découvrez tous les bons plans, réductions et avantages proposés par les commerces partenaires du pôle ${zone.center} : ${zone.cities.join(", ")}. 100 % Eure-et-Loir.`;
    document.title = title;
    let md = document.querySelector('meta[name="description"]');
    if (!md) { md = document.createElement("meta"); md.name = "description"; document.head.appendChild(md); }
    md.setAttribute("content", desc);
    let cn = document.querySelector('link[rel="canonical"]');
    if (!cn) { cn = document.createElement("link"); cn.setAttribute("rel", "canonical"); document.head.appendChild(cn); }
    cn.setAttribute("href", `${window.location.origin}/pole/${poleId}`);
    const ld = {
      "@context": "https://schema.org",
      "@type": "Place",
      "name": zone.name,
      "address": { "@type": "PostalAddress", "addressLocality": zone.center, "addressRegion": "Centre-Val de Loire", "addressCountry": "FR" },
      ...(coords ? { "geo": { "@type": "GeoCoordinates", "latitude": coords.lat, "longitude": coords.lng } } : {}),
      "containedInPlace": { "@type": "AdministrativeArea", "name": "Eure-et-Loir" },
      ...(data?.businesses?.length ? {
        "hasMap": `${window.location.origin}/villes`,
        "containsPlace": data.businesses.slice(0, 20).map((b) => ({
          "@type": "LocalBusiness", "name": b.name,
          "address": { "@type": "PostalAddress", "addressLocality": b.city || zone.center, "addressCountry": "FR" },
          ...(b.logo ? { "image": b.logo } : {}),
        })),
      } : {}),
    };
    let script = document.getElementById("pole-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.id = "pole-jsonld"; script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => { if (script) script.textContent = ""; };
  }, [zone, coords, data]);

  if (err) {
    return (
      <div className="max-w-2xl mx-auto py-20 px-4 text-center" data-testid="pole-error">
        <div className="text-2xl font-heading font-extrabold text-rose-700">Pôle introuvable</div>
        <p className="mt-2 text-slate-600">{err}</p>
        <Link to="/villes" className="mt-4 inline-block text-[#E05638] font-semibold">← Voir les 9 pôles</Link>
      </div>
    );
  }
  if (!data || !zone) {
    return <div className="max-w-2xl mx-auto py-20 text-center text-slate-500">Chargement du pôle…</div>;
  }

  return (
    <div data-testid={`pole-page-${zone.id}`}>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-[#E05638] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <button onClick={() => navigate("/villes")} className="inline-flex items-center gap-1 text-white/80 hover:text-white text-sm mb-4" data-testid="pole-back">
            <ChevronLeft size={14}/> Tous les pôles
          </button>
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/25 text-white text-xs font-bold uppercase tracking-widest">
                <MapPin size={12}/> Pôle {zone.center} · Eure-et-Loir
              </span>
              <h1 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
                Bons plans à <span className="text-amber-300">{zone.center}</span> et ses environs
              </h1>
              <p className="mt-3 text-white/85 max-w-2xl">
                {data.offers_total > 0
                  ? `${data.offers_total} bon${data.offers_total>1?"s":""} plan${data.offers_total>1?"s":""} actif${data.offers_total>1?"s":""} `
                  : "Bientôt "}
                proposés par les commerces partenaires du secteur : {zone.cities.slice(0, 6).join(", ")}
                {zone.cities.length > 6 ? "…" : ""}.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to={`/bons-plans?city=${encodeURIComponent(zone.center)}`}>
                  <Button className="btn-shine text-white h-11 px-5" data-testid="pole-cta-deals">
                    <Tag size={16} className="mr-2"/> Voir les {data.offers_total || ""} bons plans <ArrowRight size={14} className="ml-1"/>
                  </Button>
                </Link>
                <Link to="/inscription">
                  <Button variant="outline" className="bg-white/10 border-white/40 text-white hover:bg-white/20 h-11 px-5" data-testid="pole-cta-signup">
                    <ShieldCheck size={16} className="mr-2"/> Créer mon compte gratuit
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-5">
                <div className="text-xs font-bold uppercase tracking-widest text-amber-300">Le pôle en chiffres</div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-white/10 p-3">
                    <div className="font-heading text-3xl font-extrabold">{data.offers_total}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/80 mt-0.5 font-bold">Bons plans</div>
                  </div>
                  <div className="rounded-xl bg-white/10 p-3">
                    <div className="font-heading text-3xl font-extrabold">{data.businesses_total}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/80 mt-0.5 font-bold">Partenaires</div>
                  </div>
                  <div className="rounded-xl bg-white/10 p-3 col-span-2">
                    <div className="font-heading text-3xl font-extrabold">{zone.cities.length}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/80 mt-0.5 font-bold">Communes couvertes</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMMUNES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-700">
          <Building2 size={12}/> Communes du pôle
        </div>
        <h2 className="mt-2 font-heading text-2xl md:text-3xl font-extrabold text-slate-900">
          Les {zone.cities.length} communes du secteur {zone.center}
        </h2>
        <div className="mt-5 flex flex-wrap gap-2" data-testid="pole-cities-list">
          {zone.cities.map((c) => (
            <Link key={c} to={`/bons-plans/ville/${citySlug(c)}`} data-testid={`pole-city-${citySlug(c)}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-[#E05638] hover:bg-orange-50 text-sm text-slate-800 transition-colors">
              <MapPin size={12}/> {c}
            </Link>
          ))}
        </div>
      </section>

      {/* OFFRES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#E05638]">
              <Tag size={12}/> Bons plans
            </div>
            <h2 className="mt-1 font-heading text-2xl md:text-3xl font-extrabold text-slate-900">
              Bons plans à {zone.center} et ses environs
            </h2>
          </div>
          <Link to={`/bons-plans?city=${encodeURIComponent(zone.center)}`} className="text-[#E05638] font-semibold hover:underline inline-flex items-center gap-1 text-sm" data-testid="pole-see-all-offers">
            Tout voir <ArrowRight size={14}/>
          </Link>
        </div>
        {data.offers.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center bg-white" data-testid="pole-no-offers">
            <Tag size={40} className="mx-auto text-slate-300"/>
            <div className="mt-3 font-heading font-extrabold text-slate-900">Aucun bon plan actif pour le moment</div>
            <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">Les commerces du pôle {zone.center} n'ont pas encore publié d'offre. Revenez bientôt !</p>
            <Link to="/devenir-partenaire" className="mt-4 inline-block text-[#E05638] font-semibold hover:underline text-sm">
              Vous êtes commerçant à {zone.center} ? Rejoignez le réseau →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="pole-offers-grid">
            {data.offers.slice(0, 12).map((o) => (<OfferCard key={o.id} offer={o}/>))}
          </div>
        )}
      </section>

      {/* COMMERCES PARTENAIRES */}
      {data.businesses.length > 0 && (
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
              <div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-700">
                  <Store size={12}/> Le réseau local
                </div>
                <h2 className="mt-1 font-heading text-2xl md:text-3xl font-extrabold text-slate-900">
                  Les {data.businesses_total} commerces partenaires du pôle
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pole-businesses-grid">
              {data.businesses.slice(0, 12).map((b) => (
                <Link key={b.id} to={`/commerce/${b.id}`} className="group bg-slate-50 hover:bg-white border border-slate-200 hover:border-[#E05638] rounded-2xl p-4 flex items-center gap-4 transition-colors" data-testid={`pole-business-${b.id}`}>
                  {b.logo ? (
                    <img src={b.logo} alt={b.name} className="w-14 h-14 rounded-xl object-contain bg-white border border-slate-200 shrink-0" loading="lazy"/>
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-orange-50 grid place-items-center text-[#E05638] shrink-0"><Store size={22}/></div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-extrabold text-slate-900 truncate">{b.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <MapPin size={10}/> {b.city}
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-slate-400 group-hover:text-[#E05638] shrink-0"/>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA PARTENAIRE */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-900 via-slate-900 to-emerald-950 text-white p-8 md:p-12 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-amber-300 text-xs font-bold uppercase tracking-widest">
              <Award size={12}/> Commerçant à {zone.center} ?
            </div>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-extrabold">
              Rejoignez les commerces qui font vivre le local
            </h2>
            <p className="mt-2 text-white/85 text-sm">
              Partenaire Fondateur : <FounderPriceInline dark /> · offres illimitées · pas de commission sur les ventes.
            </p>
            <p className="mt-2 font-semibold text-emerald-300 text-sm" data-testid="pole-free-org-line">
              {FREE_ORG_TEXT}
            </p>
            <Link to="/organismes-collectivites" className="mt-2 inline-flex text-xs text-white/80 underline hover:text-white">
              Créer mon espace organisme
            </Link>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/devenir-partenaire">
              <Button className="btn-shine text-white h-12 px-6" data-testid="pole-partner-cta">
                <Handshake size={16} className="mr-2"/> Devenir partenaire <ArrowRight size={14} className="ml-1"/>
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
